import type { IHubCatalog, IHubCatalogType } from "@models";
import type { HubManagerTabId } from "@shared";
import { useQueries } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  areHubManagerCatalogTypeListsEqual,
  buildHubManagerCatalogSectionSearchPayload,
  HUB_CATALOG_TYPE_FILTER_ALL,
  HUB_EXPLORER_CATALOGS_PREVIEW_LIMIT,
  HUB_MANAGER_CATALOGS_PREVIEW_LIMIT,
  HUB_MANAGER_CATALOGS_VIEW_ALL_LIMIT,
  hubManagerCatalogSectionHasItems,
  isHubCatalogTypeFilterAll,
} from "./hubManagerCatalogFilters";
import {
  fetchHubCatalogSearchPage,
  getHubCatalogSearchQueryKey,
} from "./hubManagerCatalogSearch";

export interface IHubManagerCatalogSection {
  catalogType: IHubCatalogType;
  catalogs: IHubCatalog[];
  totalCount: number;
  /** True only while the section has no cached data yet (initial load). */
  isLoading: boolean;
  /** True while refetching with existing data still shown. */
  isRefetching: boolean;
  isSuccess: boolean;
  onViewAll: () => void;
  previewLimit: number;
}

interface UseHubManagerCatalogSectionsArgs {
  catalogTypes: IHubCatalogType[];
  activeTab: HubManagerTabId;
  searchQuery: string;
  userId: string;
  enabled: boolean;
  selectedTypeIds: string[];
}

export function useHubManagerCatalogSections({
  catalogTypes,
  activeTab,
  searchQuery,
  userId,
  enabled,
  selectedTypeIds,
}: UseHubManagerCatalogSectionsArgs) {
  const [viewAllTypeIds, setViewAllTypeIds] = useState<Set<number>>(
    () => new Set(),
  );
  const lastSectionDataByTypeIdRef = useRef<
    Map<number, { catalogs: IHubCatalog[]; totalCount: number }>
  >(new Map());

  const selectedTypeIdsKey = selectedTypeIds.join("\0");

  useEffect(() => {
    setViewAllTypeIds((previous) =>
      previous.size === 0 ? previous : new Set(),
    );
    lastSectionDataByTypeIdRef.current = new Map();
  }, [activeTab, searchQuery, selectedTypeIdsKey, userId]);

  const isSpecificCatalogTypeFilter =
    !isHubCatalogTypeFilterAll(selectedTypeIds);

  const selectedCatalogTypeIdSet = useMemo(() => {
    if (!isSpecificCatalogTypeFilter) {
      return new Set<string>();
    }
    return new Set(
      selectedTypeIds.filter(
        (typeId) => typeId !== HUB_CATALOG_TYPE_FILTER_ALL,
      ),
    );
  }, [isSpecificCatalogTypeFilter, selectedTypeIds]);

  const onViewAll = useCallback((typeId: number) => {
    setViewAllTypeIds((prev) => {
      if (prev.has(typeId)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(typeId);
      return next;
    });
  }, []);

  const queryConfigs = useMemo(() => {
    if (!enabled) {
      return [];
    }

    return catalogTypes.map((catalogType) => {
      const typeId = catalogType.typeId ?? -1;
      const isExplorer = activeTab === "explorer";
      const fetchFullCatalogListForType =
        isSpecificCatalogTypeFilter &&
        selectedCatalogTypeIdSet.has(String(typeId));
      const defaultPreviewLimit = isExplorer
        ? HUB_EXPLORER_CATALOGS_PREVIEW_LIMIT
        : HUB_MANAGER_CATALOGS_PREVIEW_LIMIT;
      const previewLimit = fetchFullCatalogListForType
        ? HUB_MANAGER_CATALOGS_VIEW_ALL_LIMIT
        : defaultPreviewLimit;
      const limit =
        viewAllTypeIds.has(typeId) || fetchFullCatalogListForType
          ? HUB_MANAGER_CATALOGS_VIEW_ALL_LIMIT
          : previewLimit;
      const startIndex = 0;
      const payload = buildHubManagerCatalogSectionSearchPayload(
        activeTab,
        catalogType,
        searchQuery,
        userId,
        limit,
        startIndex,
      );

      return {
        catalogType,
        typeId,
        previewLimit,
        payload,
        queryKey: getHubCatalogSearchQueryKey(payload),
        enabled: catalogType.typeId != null,
      };
    });
  }, [
    activeTab,
    catalogTypes,
    enabled,
    isSpecificCatalogTypeFilter,
    searchQuery,
    selectedCatalogTypeIdSet,
    userId,
    viewAllTypeIds,
  ]);

  const catalogQueries = useQueries({
    queries: queryConfigs.map((config) => ({
      queryKey: config.queryKey,
      queryFn: () => fetchHubCatalogSearchPage(config.payload),
      enabled: config.enabled,
      placeholderData: (
        previousData: Awaited<ReturnType<typeof fetchHubCatalogSearchPage>>,
      ) => previousData,
    })),
  });

  const sections = useMemo((): IHubManagerCatalogSection[] => {
    const lastSectionDataByTypeId = lastSectionDataByTypeIdRef.current;

    return queryConfigs.map((config, index) => {
      const query = catalogQueries[index];
      const rawItems = query?.data?.items ?? [];
      const queryCatalogs = rawItems.filter(
        (item): item is IHubCatalog => item != null && typeof item === "object",
      ) as IHubCatalog[];
      const queryTotalCount = query?.data?.totalCount ?? 0;
      const isBusy = Boolean(query?.isPending || query?.isFetching);
      const cached = lastSectionDataByTypeId.get(config.typeId);

      if (queryCatalogs.length > 0 || queryTotalCount > 0) {
        lastSectionDataByTypeId.set(config.typeId, {
          catalogs: queryCatalogs,
          totalCount: queryTotalCount,
        });
      }

      const catalogs =
        queryCatalogs.length > 0
          ? queryCatalogs
          : isBusy && cached
            ? cached.catalogs
            : queryCatalogs;
      const totalCount =
        queryTotalCount > 0
          ? queryTotalCount
          : isBusy && cached
            ? cached.totalCount
            : queryTotalCount;

      return {
        catalogType: config.catalogType,
        catalogs,
        totalCount,
        isLoading: Boolean(query?.isPending),
        isRefetching: Boolean(query?.isFetching && !query?.isPending),
        isSuccess: Boolean(query?.isSuccess),
        onViewAll: () => onViewAll(config.typeId),
        previewLimit: config.previewLimit,
      };
    });
  }, [catalogQueries, onViewAll, queryConfigs]);

  const sectionsQueriesSettled = useMemo(() => {
    if (!enabled || catalogTypes.length === 0) {
      return true;
    }
    if (sections.length !== catalogTypes.length) {
      return false;
    }
    return sections.every((section) => !section.isLoading);
  }, [catalogTypes.length, enabled, sections]);

  const sectionsPending = !sectionsQueriesSettled;

  const resolvedCatalogTypesWithItems = useMemo(
    () =>
      sections
        .filter((section) => hubManagerCatalogSectionHasItems(section))
        .map((section) => section.catalogType),
    [sections],
  );

  const [catalogTypesWithItems, setCatalogTypesWithItems] = useState<
    IHubCatalogType[]
  >([]);

  useEffect(() => {
    if (enabled) {
      return;
    }
    setCatalogTypesWithItems((previous) =>
      previous.length === 0 ? previous : [],
    );
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (sectionsQueriesSettled) {
      setCatalogTypesWithItems((previous) =>
        areHubManagerCatalogTypeListsEqual(
          previous,
          resolvedCatalogTypesWithItems,
        )
          ? previous
          : resolvedCatalogTypesWithItems,
      );
      return;
    }

    setCatalogTypesWithItems((previous) => {
      const finishedZeroTypeIds = new Set(
        sections
          .filter(
            (section) =>
              section.isSuccess &&
              !section.isLoading &&
              Number(section.totalCount) === 0,
          )
          .map((section) => section.catalogType.typeId),
      );

      const next = previous.filter(
        (catalogType) => !finishedZeroTypeIds.has(catalogType.typeId),
      );

      return areHubManagerCatalogTypeListsEqual(previous, next)
        ? previous
        : next;
    });
  }, [
    enabled,
    resolvedCatalogTypesWithItems,
    sections,
    sectionsQueriesSettled,
  ]);

  return {
    sections,
    sectionsPending,
    sectionsQueriesSettled,
    catalogTypesWithItems,
  };
}
